/**
 * Silospot CLD design-system kit. Token-only colours (light theme, accent blue
 * #2563eb; risk palette green/amber/red/purple/cyan) so the look is driven by
 * packages/ui globals.css. Mirrors the locked CLD mockup component set.
 */
import { useTranslation } from '@silospot/i18n/react';
import { cn } from '@silospot/ui/lib/utils';
import type { LucideIcon } from 'lucide-react';
import { type ButtonHTMLAttributes, type ReactNode, useId, useMemo, useState } from 'react';

// --- Tone palette ----------------------------------------------------------
export type Tone = 'neutral' | 'blue' | 'clean' | 'suspect' | 'fraud' | 'bot' | 'info';

const TONE_CLASS: Record<Tone, string> = {
  neutral: 'bg-neutral-bg text-neutral-fg',
  blue: 'bg-accent-bg text-accent-strong',
  clean: 'bg-clean-bg text-clean-fg',
  suspect: 'bg-suspect-bg text-suspect-fg',
  fraud: 'bg-fraud-bg text-fraud-fg',
  bot: 'bg-bot-bg text-bot-fg',
  info: 'bg-info-bg text-info-fg',
};

// --- Card ------------------------------------------------------------------
export function Card({
  children,
  className,
  hover,
}: {
  children: ReactNode;
  className?: string;
  hover?: boolean;
}) {
  return (
    <div
      className={cn(
        'min-w-0 rounded-lg border border-border bg-surface p-[18px] shadow-[var(--shadow)]',
        hover &&
          'cursor-pointer transition-[border-color,box-shadow] hover:border-border-strong hover:shadow-[0_4px_14px_rgba(16,24,40,0.07)]',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function CardHead({
  title,
  sub,
  right,
  hint,
}: {
  title: ReactNode;
  sub?: ReactNode;
  right?: ReactNode;
  hint?: string;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="inline-flex items-center gap-1.5 text-[14.5px] font-semibold tracking-[-0.01em] text-text">
          {title}
          {hint ? <Hint text={hint} /> : null}
        </div>
        {sub ? <div className="mt-0.5 text-[12.5px] text-text-3">{sub}</div> : null}
      </div>
      {right}
    </div>
  );
}

// --- Page header -----------------------------------------------------------
export function PageHeader({
  title,
  sub,
  actions,
}: {
  title: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="m-0 text-[21px] font-bold tracking-[-0.02em] text-text">{title}</h1>
        {sub ? <p className="mt-1 max-w-[620px] text-[13.5px] text-text-2">{sub}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

// --- KPI -------------------------------------------------------------------
export function KpiGrid({ children, cols = 6 }: { children: ReactNode; cols?: 3 | 4 | 6 }) {
  const lg = cols === 6 ? 'lg:grid-cols-6' : cols === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3';
  return <div className={cn('grid grid-cols-2 gap-3.5 md:grid-cols-3', lg)}>{children}</div>;
}

export function Kpi({
  label,
  value,
  delta,
  deltaTone = 'up',
  sub,
  icon: Icon,
}: {
  label: ReactNode;
  value: ReactNode;
  delta?: ReactNode;
  deltaTone?: 'up' | 'down';
  sub?: ReactNode;
  icon?: LucideIcon;
}) {
  return (
    <Card className="flex flex-col gap-2.5 p-4">
      <div className="flex items-center justify-between">
        <span className="text-[12.5px] font-medium text-text-2">{label}</span>
        {Icon ? <Icon className="h-[17px] w-[17px] text-text-3" /> : null}
      </div>
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-mono text-[26px] font-bold tracking-[-0.02em] text-text tabular-nums">
          {value}
        </span>
        {delta != null ? (
          <span
            className={cn(
              'text-[12px] font-semibold',
              deltaTone === 'down' ? 'text-fraud-fg' : 'text-clean-fg',
            )}
          >
            {delta}
          </span>
        ) : null}
      </div>
      {sub ? <div className="text-[11.5px] text-text-3">{sub}</div> : null}
    </Card>
  );
}

// --- Badge / pills ---------------------------------------------------------
export function Badge({
  children,
  tone = 'neutral',
  dot,
}: {
  children: ReactNode;
  tone?: Tone;
  dot?: boolean;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold',
        TONE_CLASS[tone],
      )}
    >
      {dot ? <span className="h-1.5 w-1.5 rounded-full bg-current" /> : null}
      {children}
    </span>
  );
}

/** Risk score 0-100 as a bar + mono number, coloured by the 3-band shorthand. */
export function RiskPill({ value, small }: { value: number; small?: boolean }) {
  const color =
    value >= 70 ? 'var(--fraud-fg)' : value >= 40 ? 'var(--suspect-fg)' : 'var(--clean-fg)';
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={cn(
          'overflow-hidden rounded-full bg-neutral-bg',
          small ? 'w-[30px]' : 'w-[38px]',
        )}
        style={{ height: 6 }}
      >
        <span
          className="block h-full"
          style={{ width: `${Math.max(0, Math.min(100, value))}%`, background: color }}
        />
      </span>
      <span className="font-mono text-[12px] font-semibold tabular-nums" style={{ color }}>
        {value}
      </span>
    </span>
  );
}

// --- Probabilistic verdict (5 levels) --------------------------------------
type VerdictLevel = { min: number; key: string; color: string };
const VERDICTS: VerdictLevel[] = [
  { min: 0, key: 'low', color: '#16a34a' },
  { min: 25, key: 'suspect', color: '#d97706' },
  { min: 50, key: 'high', color: '#ea580c' },
  { min: 75, key: 'probable', color: '#dc2626' },
  { min: 90, key: 'confirmed', color: '#991b1b' },
];

export function verdictOf(score: number): VerdictLevel {
  let v: VerdictLevel = { min: 0, key: 'low', color: '#16a34a' };
  for (const t of VERDICTS) if (score >= t.min) v = t;
  return v;
}

export function Verdict({
  score,
  confirmed,
  showScore = true,
  size = 'md',
}: {
  score: number;
  confirmed?: boolean;
  showScore?: boolean;
  size?: 'sm' | 'md';
}) {
  const { t } = useTranslation('design');
  const v = verdictOf(confirmed ? 95 : score);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-semibold',
        size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-[3px] text-[11.5px]',
      )}
      style={{ color: v.color, background: `${v.color}14` }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: v.color }} />
      {t(`cld.verdict.${v.key}`)}
      {showScore ? <span className="font-mono opacity-70 tabular-nums">{score}</span> : null}
    </span>
  );
}

/** Horizontal 5-segment scale with a score marker. */
export function VerdictScale({ score }: { score: number }) {
  const { t } = useTranslation('design');
  const active = verdictOf(score);
  const widths = [25, 25, 25, 15, 10];
  return (
    <div>
      <div className="mb-[7px] flex items-center justify-between">
        <span className="text-[12.5px] text-text-2">{t('cld.scale.title')}</span>
        <Verdict score={score} />
      </div>
      <div className="flex h-2 gap-0.5 overflow-hidden rounded-full">
        {VERDICTS.map((v, i) => (
          <span
            key={v.key}
            style={{
              flex: widths[i],
              background: v.color,
              opacity: active.min === v.min ? 1 : 0.22,
            }}
          />
        ))}
      </div>
      <div className="relative h-3">
        <span
          className="absolute top-px h-0 w-0 border-x-[5px] border-t-[6px] border-x-transparent border-t-text"
          style={{ left: `calc(${Math.max(0, Math.min(100, score))}% - 5px)` }}
        />
      </div>
      <div className="flex justify-between text-[10px] text-text-3">
        <span>{t('cld.scale.low')}</span>
        <span>{t('cld.scale.suspect')}</span>
        <span>{t('cld.scale.high')}</span>
        <span>{t('cld.scale.probable')}</span>
        <span>{t('cld.scale.confirmed')}</span>
      </div>
    </div>
  );
}

export function EstimatedNote() {
  const { t } = useTranslation('design');
  return (
    <div className="inline-flex items-center gap-2 rounded-md border border-[#f6e2c4] bg-[#fdf4e7] px-[11px] py-[7px] text-[11.5px] leading-snug text-suspect-fg">
      <span className="shrink-0">ⓘ</span>
      <span>{t('cld.estimated')}</span>
    </div>
  );
}

// --- Buttons ---------------------------------------------------------------
export function Btn({
  children,
  primary,
  danger,
  icon: Icon,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  primary?: boolean;
  danger?: boolean;
  icon?: LucideIcon;
}) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3.5 py-2 text-[13px] font-semibold transition-colors',
        primary
          ? 'bg-primary text-white hover:bg-accent-strong'
          : danger
            ? 'border border-[#f3c9c9] bg-surface text-fraud-fg hover:bg-fraud-bg'
            : 'border border-border-strong bg-surface text-[#3a4151] hover:bg-bg',
        className,
      )}
      {...rest}
    >
      {Icon ? <Icon className="h-[15px] w-[15px]" /> : null}
      {children}
    </button>
  );
}

// --- Chip filter -----------------------------------------------------------
export function Chip({
  children,
  active,
  onClick,
}: {
  children: ReactNode;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'cursor-pointer rounded-full border px-3 py-[5px] text-[12.5px] font-medium transition-colors',
        active
          ? 'border-text bg-text text-white'
          : 'border-border bg-surface text-text-2 hover:border-border-strong',
      )}
    >
      {children}
    </button>
  );
}

// --- Segmented control -----------------------------------------------------
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  size = 'md',
}: {
  options: { value: T; label: ReactNode }[];
  value: T;
  onChange: (v: T) => void;
  size?: 'sm' | 'md';
}) {
  return (
    <div className="inline-flex gap-0.5 rounded-md bg-neutral-bg p-[3px]">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded-[6px] font-semibold transition-colors',
            size === 'sm' ? 'px-2.5 py-1 text-[12px]' : 'px-3 py-1.5 text-[12.5px]',
            value === o.value
              ? 'bg-surface text-text shadow-[0_1px_2px_rgba(16,24,40,0.08)]'
              : 'text-text-2 hover:text-text',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// --- Toggle ----------------------------------------------------------------
export function Toggle({
  checked,
  onChange,
  'aria-label': ariaLabel,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  'aria-label'?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative h-[22px] w-[38px] shrink-0 rounded-full transition-colors',
        checked ? 'bg-primary' : 'bg-[#cdd2da]',
      )}
    >
      <span
        className="absolute top-0.5 h-[18px] w-[18px] rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.2)] transition-[left]"
        style={{ left: checked ? 18 : 2 }}
      />
    </button>
  );
}

// --- Code chip -------------------------------------------------------------
export function Cc({ code }: { code: ReactNode }) {
  return (
    <span className="rounded border border-border bg-neutral-bg px-1.5 py-px font-mono text-[11px] font-semibold text-text-2">
      {code}
    </span>
  );
}

// --- Hint (glossary tooltip) -----------------------------------------------
export function Hint({ text, children }: { text: string; children?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <span className="relative inline-flex cursor-help items-center align-middle">
      <button
        type="button"
        aria-describedby={open ? id : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex cursor-help"
      >
        {children ?? (
          <span className="grid h-[15px] w-[15px] place-items-center rounded-full bg-[#e7ecf3] text-[10px] font-bold text-text-2">
            i
          </span>
        )}
      </button>
      {open ? (
        <span
          id={id}
          role="tooltip"
          className="absolute bottom-[calc(100%+8px)] left-1/2 z-50 w-max max-w-[264px] -translate-x-1/2 rounded-lg bg-text px-3 py-2 text-left text-[12px] font-normal leading-snug text-[#f3f5f8] shadow-[0_8px_24px_rgba(16,24,40,0.28)]"
        >
          {text}
        </span>
      ) : null}
    </span>
  );
}

// --- Sortable data table ---------------------------------------------------
export interface Column<R> {
  key: string;
  label: ReactNode;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  sortValue?: (row: R) => string | number;
  render?: (row: R) => ReactNode;
  /** Cap the cell width and ellipsize overflow (e.g. long URLs) — full value in detail. */
  truncate?: boolean;
  /** Fixed column width (Tailwind class, e.g. `w-[92px]`) — used with the table's `dense` layout. */
  width?: string;
}

export function DataTable<R>({
  columns,
  rows,
  rowKey,
  initialSort,
  dense,
}: {
  columns: Column<R>[];
  rows: R[];
  rowKey: (row: R, i: number) => string;
  initialSort?: { key: string; dir: 'asc' | 'desc' };
  /**
   * Compact layout that keeps every column visible on desktop without a
   * horizontal scrollbar: tighter padding/type, `table-fixed` widths from
   * each column's `width` at `lg`+, and a min-width fallback so narrow
   * screens keep the usual horizontal scroll.
   */
  dense?: boolean;
}) {
  const [sort, setSort] = useState(initialSort ?? { key: columns[0]?.key ?? '', dir: 'desc' });

  const sorted = useMemo(() => {
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const val = (r: R) =>
      col.sortValue?.(r) ?? ((r as Record<string, unknown>)[col.key] as string | number);
    return [...rows].sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      const cmp =
        typeof va === 'number' && typeof vb === 'number'
          ? va - vb
          : String(va).localeCompare(String(vb));
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [columns, rows, sort]);

  return (
    <div className="overflow-x-auto">
      <table
        className={cn(
          'w-full border-collapse',
          dense
            ? 'min-w-[1120px] text-[13px] min-[1440px]:min-w-0 min-[1440px]:table-fixed'
            : 'text-[13px]',
        )}
      >
        <thead>
          <tr>
            {columns.map((c) => {
              const sortable = c.sortable !== false;
              const indicator =
                sort.key === c.key ? (
                  <span className="text-primary">{sort.dir === 'desc' ? ' ↓' : ' ↑'}</span>
                ) : null;
              return (
                <th
                  key={c.key}
                  className={cn(
                    'border-b border-border font-semibold uppercase tracking-[0.04em] text-text-3',
                    dense
                      ? 'px-2 py-2 align-bottom text-[11px] leading-[1.15]'
                      : 'whitespace-nowrap px-3 py-2.5 text-[11px]',
                    c.width,
                    c.align === 'right'
                      ? 'text-right'
                      : c.align === 'center'
                        ? 'text-center'
                        : 'text-left',
                  )}
                >
                  {sortable ? (
                    <button
                      type="button"
                      onClick={() =>
                        setSort((s) => ({
                          key: c.key,
                          dir: s.key === c.key && s.dir === 'desc' ? 'asc' : 'desc',
                        }))
                      }
                      className="inline-flex cursor-pointer select-none items-center font-semibold uppercase tracking-[0.04em] text-text-3"
                    >
                      {c.label}
                      {indicator}
                    </button>
                  ) : (
                    <>
                      {c.label}
                      {indicator}
                    </>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => (
            <tr key={rowKey(r, i)} className="hover:bg-[#fafbfc]">
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={cn(
                    'border-b border-[#f1f3f6] text-[#3a4151]',
                    dense ? 'px-2 py-1.5' : 'px-3 py-2.5',
                    c.truncate ? 'max-w-[260px] truncate' : 'whitespace-nowrap',
                    c.width,
                    c.align === 'right'
                      ? 'text-right'
                      : c.align === 'center'
                        ? 'text-center'
                        : 'text-left',
                  )}
                >
                  {c.render ? c.render(r) : ((r as Record<string, unknown>)[c.key] as ReactNode)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
